/**
 * ecosystemRouting: helpers for cross-tenant navigation URLs.
 *
 * Once subdomain mode goes live, the same Ecosystem record is reachable via
 * (in order of preference):
 *   1. a custom apex domain (e.g. `coloradostemecosystem.org`)
 *   2. a slug-based subdomain (e.g. `costem.rileconnect.org`)
 *   3. a path-mode URL on the platform apex (e.g. `rileconnect.org/costem`)
 *
 * Cross-tenant links must always go to the target ecosystem's CANONICAL host,
 * never naively prepend `/{slug}` to the current origin. A user on
 * `costem.rileconnect.org` clicking "Visit MTHV" must land on
 * `mthv.rileconnect.org/`, NOT `costem.rileconnect.org/mthv` (the second
 * shadows the wrong tenant and 404s once the path resolver runs).
 *
 * The schema fields `subdomain`, `customDomain`, `branding`, etc. are not all
 * present on every Ecosystem record yet: Wave M will land the schema
 * additions. Until then we read defensively (optional chaining) and fall back
 * to the v1 path-mode form on the platform apex.
 *
 * v1.0.1 #56: fix EcosystemDirectory cross-tenant href when rendered from a
 * tenant subdomain.
 */

// Matches a tenant subdomain like `costem.rileconnect.org`. The captured label
// is the candidate slug. Mirrors the SUBDOMAIN_RE in EcosystemContext.js so
// the two stay in lockstep.
// rileconnect.com is deliberately not here: the server redirects it to the
// .org equivalent, so the address bar is never .com by the time this runs.
// Keep in step with server/src/middleware/tenant-resolution.js.
const SUBDOMAIN_RE = /^([a-z0-9-]+)\.rileconnect\.(org|dev)$/i;

// The set of hostname labels that are NOT tenant subdomains: they identify
// the platform apex itself across environments. Kept in sync with the
// "Deployment-environment hostnames" block in server/src/config/reserved-slugs.js.
const APEX_LABELS = new Set([
    'www',
    'dev',
    'development',
    'staging',
    'stage',
    'prod',
    'production',
    'test',
    'qa',
]);

/**
 * Detect whether the current page is loaded under a tenant subdomain.
 *
 * @param {string} [hostname=window.location.hostname]
 * @returns {boolean}
 */
export function isTenantSubdomain(hostname) {
    const host =
        hostname ||
        (typeof window !== 'undefined' ? window.location.hostname : '');
    const match = host.match(SUBDOMAIN_RE);
    if (!match) return false;
    const label = match[1].toLowerCase();
    return !APEX_LABELS.has(label);
}

/**
 * Resolve the platform apex origin (scheme + host) from the current location.
 *
 * Strips a tenant subdomain label off the front of the hostname so a user on
 * `costem.rileconnect.org` gets `https://rileconnect.org` back. Apex labels
 * (`www`, `dev`, `staging`, etc.) are NOT stripped: they identify the
 * platform itself and the user is already on it.
 *
 * @param {Location} [location=window.location]
 * @returns {string} The apex origin, e.g. `https://rileconnect.org`.
 */
export function getPlatformApexOrigin(location) {
    const loc =
        location || (typeof window !== 'undefined' ? window.location : null);
    if (!loc) return '';

    const { protocol, hostname, port } = loc;
    const match = hostname.match(SUBDOMAIN_RE);

    let apexHost = hostname;
    if (match) {
        const label = match[1].toLowerCase();
        if (!APEX_LABELS.has(label)) {
            // Drop the tenant label: 'costem.rileconnect.org' -> 'rileconnect.org'
            apexHost = hostname.slice(label.length + 1);
        }
    }

    const portPart = port ? `:${port}` : '';
    return `${protocol}//${apexHost}${portPart}`;
}

/**
 * Resolve the canonical destination URL for an ecosystem.
 *
 * Resolution order:
 *   1. `customDomain` (the tenant owns its own apex)
 *   2. `subdomain` (slug-based subdomain: explicit field if present;
 *       otherwise derived from `slug` once we're confident subdomain mode is
 *       provisioned for every tenant)
 *   3. Path-mode URL on the platform apex
 *
 * Schema-field caveat: `customDomain` exists on the Ecosystem schema today;
 * `subdomain` does NOT yet (Wave M will add it). We read defensively. Until
 * `subdomain` lands, this function falls through to the path-mode form, which
 * works for tenants reachable on the apex.
 *
 * The returned URL is ALWAYS absolute when the current page is under a tenant
 * subdomain (so cross-tenant nav from `costem.rileconnect.org` to MTHV does
 * not stay on the costem host). When the current page is on the platform
 * apex, the relative `/${slug}` form is returned: a full navigation on the
 * same origin works correctly and avoids hard-coding the apex URL.
 *
 * @param {object} eco - Ecosystem record with at minimum `slug`.
 * @param {Location} [location=window.location]
 * @returns {string} The destination URL (relative `/slug` or absolute URL).
 */
export function getEcosystemDestinationUrl(eco, location) {
    if (!eco || !eco.slug) return '';
    const loc =
        location || (typeof window !== 'undefined' ? window.location : null);

    // 1. Custom apex domain: always absolute.
    if (eco.customDomain) {
        const scheme = loc ? loc.protocol : 'https:';
        return `${scheme}//${eco.customDomain}`;
    }

    // 2. Slug-based subdomain: when the field is set, prefer it.
    // (Wave M will add `subdomain` to the schema; until then this branch is
    // a no-op and we fall through to path mode.)
    if (eco.subdomain) {
        const scheme = loc ? loc.protocol : 'https:';
        return `${scheme}//${eco.subdomain}`;
    }

    // 3. Path mode. If the current page is itself on a tenant subdomain, we
    // MUST build an absolute URL pointing at the platform apex: otherwise
    // the link stays on the current tenant's host and the slug shadows the
    // wrong tenant. On the apex (or in tests with no `location`), the
    // relative `/${slug}` form is correct.
    if (loc && isTenantSubdomain(loc.hostname)) {
        return `${getPlatformApexOrigin(loc)}/${eco.slug}`;
    }

    return `/${eco.slug}`;
}

/**
 * Resolve an ALWAYS-absolute URL for an ecosystem, optionally with a suffix.
 *
 * `getEcosystemDestinationUrl` returns a relative `/${slug}` when called from
 * the platform apex, which is correct for in-app navigation but useless for a
 * link a member is meant to copy and paste into a text, an email, or a
 * LinkedIn post. This wrapper guarantees a fully-qualified URL by prefixing
 * the current origin when the underlying result is relative. Absolute results
 * (custom domain, subdomain, cross-tenant from a subdomain) pass through
 * unchanged.
 *
 * `suffix` is concatenated verbatim, so pass either a path (`/signup`) or a
 * query string (`?invite=abc123`) including its leading character.
 *
 * @param {object} eco - Ecosystem record with at minimum `slug`.
 * @param {string} [suffix=''] - Path or query to append (include the leading `/` or `?`).
 * @param {Location} [location=window.location]
 * @returns {string} An absolute URL, or `''` if the ecosystem is missing.
 */
export function getAbsoluteEcosystemUrl(eco, suffix = '', location) {
    const loc =
        location || (typeof window !== 'undefined' ? window.location : null);
    const dest = getEcosystemDestinationUrl(eco, loc);
    if (!dest) return '';

    let absolute = dest;
    // A relative `/${slug}` means we're on the platform apex: prefix the
    // current origin (protocol + host + port) so the link stands on its own.
    if (dest.startsWith('/') && loc) {
        const portPart = loc.port ? `:${loc.port}` : '';
        absolute = `${loc.protocol}//${loc.hostname}${portPart}${dest}`;
    }

    if (!suffix) return absolute;

    // When appending a query string to a bare-host URL (custom domain or
    // subdomain, which have no path segment), insert a root slash so the
    // result is `https://host/?q` rather than the uglier `https://host?q`.
    // Path-mode URLs already carry a `/${slug}` path, so they are left alone.
    const hasPath = /^[a-z][a-z0-9+.-]*:\/\/[^/]+\//i.test(absolute);
    if (suffix.startsWith('?') && !hasPath) {
        absolute = `${absolute}/`;
    }

    return `${absolute}${suffix}`;
}

export default getEcosystemDestinationUrl;
